import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface Group {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  _count: { members: number; devices: number };
}

interface PaginatedGroups {
  data: Group[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GroupFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useGroups(filters: GroupFilters) {
  return useQuery({
    queryKey: ['groups', filters],
    queryFn: () =>
      api.get<PaginatedGroups>('/groups', {
        query: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 20,
          search: filters.search,
        },
      }),
    placeholderData: (prev) => prev,
  });
}

export interface GroupDetail extends Group {
  members: {
    addedAt: string;
    user: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      role: string;
      active: boolean;
    };
  }[];
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: ['groups', id],
    queryFn: () => api.get<GroupDetail>(`/groups/${id}`),
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) => api.post<Group>('/groups', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name?: string; description?: string | null } }) =>
      api.patch<Group>(`/groups/${id}`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: ['groups', vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.put<void>(`/groups/${groupId}/members/${userId}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['groups', vars.groupId] }),
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.delete<void>(`/groups/${groupId}/members/${userId}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['groups', vars.groupId] }),
  });
}
